import React, { useState, useEffect, useRef, useCallback } from "react";
import { View, StyleSheet, Dimensions, ScrollView, Platform, Text, Linking, Pressable } from "react-native";
import { useNavigation } from "@react-navigation/native";
import { HeaderBackButton } from "@react-navigation/elements";
import { COLORS, STRINGS } from "../../../constants";
import {
  //faImage,
  faCar,
  faDirections,
  faLocationDot,
  faShareAlt,
  faParking,
  faBuilding,
  faGauge,
  faRoad,
} from "@fortawesome/free-solid-svg-icons";
import Heading from "../../../components/Heading/Heading";
import DetailButtonArray from "../../../components/DetailButtonArray/DetailButtonArray";
import DetailInfoCard from "../../../components/DetailInfoCard/DetailInfoCard";
import SummaryCard from "../../../components/SummaryCard/SummaryCard";
import DetailsMapView from "../../Other/DetailsMapView/DetailsMapView";
import { parkingFormatAddress, getParkingTypeLabel } from "../../../utils/formatting";
import ParkingStore from "../../../storage/parkingStore";
import { openDirections, share, supportsShare } from "../../../utils/helper";
import { getCurrentLocation, getDistanceFromLocation } from "../../../utils/geolocation";
import Loading from "../../../components/Loading/Loading";
import PageNotFound from "../../../components/NoResults/PageNotFound";
import { logAnalyticsEvent } from "../../../utils/firebase";
import useMetaDescriptionOnFocus from "../../../components/Meta/MetaDescription";
import ParkingDetailDisclaimer from "../../../components/ParkingDisclaimer/ParkingDetailDisclaimer";

/**
 * ParkingDetailViewCommon
 *
 * This component displays detailed information about a specific parking location.
 * It fetches and displays the distance to the parking location, the availability of parking spaces,
 * and provides options for directions, sharing, and payment.
 *
 * @param {Object} props - The component props.
 * @param {Object} props.route - The route object containing navigation parameters.
 * @param {Function} props.FontAwesomeIcon - The FontAwesomeIcon component for rendering icons.
 * @param {string|number} props.iconSize - The size of the icons to be rendered.
 *
 * @returns {JSX.Element} The rendered component.
 */
const ParkingDetailViewCommon = ({ route, FontAwesomeIcon, iconSize }) => {
  logAnalyticsEvent("screen_view", { name: "VBGo-ParkingDetailView" });

  const text2PayRef = useRef(null);
  const navigation = useNavigation();
  const [distance, setDistance] = useState(null);
  const [parkingDisclaimer, setParkingDisclaimer] = useState(false);
  const parkingData = ParkingStore((state) => state.parking);
  const parkingIndex =
    route.params.index === undefined
      ? parkingData.findIndex((p) => p.name === decodeURIComponent(route.params.name))
      : route.params.index;
  const parking = parkingData[parkingIndex];
  const loadingParking = ParkingStore((state) => state.loadingParking);
  useMetaDescriptionOnFocus(parking?.name);

  /**
   * Initializes an array of header button configurations based on the available parking data.
   *
   * Each button object may include:
   * - Directions: If latitude and longitude are present.
   * - Share: If sharing is supported.
   * - Photos: If photos are available.
   *
   *
   * @returns {Array<Object>} Array of button configuration objects for the header.
   */
  const initHeaderButtons = () => {
    return [
      parking.latitude && parking.longitude
        ? { icon: faDirections, label: "Directions", color: COLORS.border, testID: "button-directions" }
        : null,

      supportsShare() ? { icon: faShareAlt, label: "Share", color: COLORS.border, testID: "button-share" } : null,
      // The Photos button is currently disabled
      // parking?.images ? { icon: faImage, label: "Photos", color: COLORS.border, testID: "button-photos" } : null,
    ].filter(Boolean);
  };

  const parkingTypeIcons = {
    meter: faGauge,
    structure: faBuilding,
    lot: faCar,
    street: faRoad,
    default: faParking,
  };

  const text2PayUrl = parking?.text2Pay ? `https://prkg.io/${parking.text2Pay}` : null;

  /**
   * handlePress
   *
   * This function is a simple handler for button press events. It logs the label of the pressed
   * button to the console for debugging or action tracking purposes.
   */
  const handlePress = (button) => {
    if (button.label === "Directions") {
      openDirections(parking.latitude, parking.longitude);
    }
    if (button.label === "Share") {
      const url = `\n\n${process.env.REACT_APP_APPURL}/parking/details?name=${encodeURIComponent(parking.name)}`;
      const msg = `Check out: ${parking.name} at ${parking.address.replace(/\n/g, ", ")}${url}`;
      share(msg, parking.name);
    }
    // The Photos button is currently disabled
    // if (button.label === "Photos") {
    //   navigation.navigate("ImageGalleryModal", {
    //     data: parking,
    //     accessibilityLabel: `${parking.name} ${STRINGS.parkingImages}`,
    //   });
    // }
  };

  const text2PayPress = () => {
    if (text2PayUrl) {
      text2PayRef.current?.blur?.();
      setParkingDisclaimer(true);
    }
  };

  const text2PayOnConfirm = async () => {
    try {
      setParkingDisclaimer(false);
      await Linking.openURL(text2PayUrl);
    } catch (err) {
      console.error("Failed to open text2pay", err);
    }
  };

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Enter" && parkingDisclaimer) {
        text2PayOnConfirm();
      }
    },
    [parkingDisclaimer, text2PayOnConfirm]
  );

  useEffect(() => {
    const getDistance = async () => {
      if (!parking) return;
      try {
        const userLocation = await getCurrentLocation();
        setDistance(await getDistanceFromLocation(userLocation, { lat: parking.latitude, lng: parking.longitude }));
      } catch (err) {
        return null;
      }
    };

    getDistance();
  }, [parking]);

  useEffect(() => {
    if (parking) {
      navigation.setOptions({
        title: parking?.name,
        headerRight: () => (
          <DetailButtonArray
            FontAwesomeIcon={FontAwesomeIcon}
            buttons={initHeaderButtons()}
            onPress={handlePress}
            iconSize={Platform.OS === "web" ? "2x" : iconSize}
          />
        ),
        headerRightContainerStyle: styles.headerRight,
        headerTitleStyle: styles.headerTitle,
      });
    }
  }, [parking]);

  useEffect(() => {
    navigation.setOptions({
      headerLeft: () => (
        <View style={{ flexDirection: "row", alignItems: "center" }}>
          <HeaderBackButton
            onPress={() =>
              navigation.canGoBack()
                ? navigation.goBack()
                : (window.location.href = `${window.location.origin}/parking`)
            }
            tintColor={COLORS.textDarker}
          />
          {distance !== null && (
            <View style={{ flexDirection: "row", alignItems: "center" }}>
              {Platform.OS === "ios" && <Text style={styles.dotStyle}> • </Text>}
              <Text style={styles.distanceText}>{`${distance} ${STRINGS.milesAway}`}</Text>
            </View>
          )}
        </View>
      ),
    });
  }, [distance]);

  useEffect(() => {
    if (Platform.OS === "web") {
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    } else {
      return;
    }
  }, [handleKeyDown]);

  if (loadingParking) {
    return (
      <View style={styles.container}>
        <Loading />
      </View>
    );
  }

  if (!parking) {
    return (
      <View style={styles.container}>
        <PageNotFound />
      </View>
    );
  }

  return (
    <ScrollView contentContainerStyle={styles.scrollContainer}>
      <ParkingDetailDisclaimer
        visible={parkingDisclaimer}
        onDismiss={() => setParkingDisclaimer(false)}
        onConfirm={text2PayOnConfirm}
      />
      <View style={styles.container}>
        <View style={styles.mapContainer}>
          <DetailsMapView
            name={parking.name}
            lat={parking.latitude}
            lng={parking.longitude}
            screen={STRINGS.parkingRoute}
            type={parking.type}
            color={parking.statusColor}
          />
        </View>
        <Heading text={parking?.name} fontSize={26} />
        <View style={styles.text2payContainer}>
          {text2PayUrl && (
            <Pressable
              ref={text2PayRef}
              style={styles.text2pay}
              onPress={text2PayPress}
              accessibilityLabel={`${parking.name} ${STRINGS.text2Pay}`}
            >
              <Text style={styles.text2PayButtonText}>{STRINGS.pay}</Text>
            </Pressable>
          )}
        </View>
        <View style={styles.cardsContainer}>
          <SummaryCard
            heading={STRINGS.address}
            description={parkingFormatAddress(parking?.address) || "No address provided."}
            icon={faLocationDot}
            size={iconSize}
            iconColor={COLORS.primary}
            FontAwesomeIcon={FontAwesomeIcon}
          />
        </View>
        <View style={styles.detailsContainer}>
          <DetailInfoCard
            title={STRINGS.parkingType}
            subtitle={`${getParkingTypeLabel(parking)}`}
            FontAwesomeIcon={FontAwesomeIcon}
            iconName={parkingTypeIcons[parking.type] || parkingTypeIcons.default}
            iconColor={COLORS.primary}
            iconSize={iconSize}
            showDivider={false}
          />
        </View>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContainer: {
    flexGrow: 1,
    justifyContent: "flex-start",
    paddingBottom: Dimensions.get("window").height * 0.1,
  },
  chipContainer: {
    flexDirection: "row",
    gap: 8,
    paddingTop: 12,
  },
  chipScrollView: {
    flexGrow: 0,
  },
  chip: {
    marginRight: 8,
  },
  detailsContainer: {
    marginBottom: 24,
  },
  cardsContainer: {
    paddingTop: 16,
    paddingBottom: 16,
  },
  mapContainer: {
    height: 200,
    marginBottom: 10,
  },
  distanceText: {
    fontSize: 16,
    color: COLORS.textDarker,
  },
  dotStyle: {
    fontSize: 18,
    color: COLORS.textDarker,
  },
  text2payContainer: {
    flexDirection: "column",
    gap: 16,
  },
  text2pay: {
    alignItems: "center",
    marginRight: 18,
    padding: 8,
    paddingLeft: 32,
    paddingRight: 32,
    borderRadius: 30,
    backgroundColor: COLORS.primary,
    alignSelf: "flex-start",
  },
  text2PayButtonText: {
    color: COLORS.textLight,
    fontSize: 22,
  },
  headerRight: {
    flexDirection: "row",
    alignItems: "center",
    paddingRight: 8,
    justifyContent: "flex-end",
  },
  headerTitle: {
    display: "none",
  },
});

export default ParkingDetailViewCommon;
